import { ApiResponse } from '../../types/api';
import {
  DashboardFilter,
  AdminDashboardData,
  OfficerDashboardData,
  SupervisorDashboardData,
  VendorDashboardData,
  ReportType,
  ExportConfig,
} from '../../types/dashboard';
import { IReportsAdapter } from './IReportsAdapter';

export class LaravelReportsAdapter implements IReportsAdapter {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  }

  private async request<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val) url.searchParams.set(key, val);
      });
    }
    const token = localStorage.getItem('auth_token');
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Request failed' }));
      return { success: false, data: null as any, message: err.message || 'Error', errors: [String(res.status)] };
    }
    return res.json();
  }

  async getAdminDashboard(filter?: DashboardFilter): Promise<ApiResponse<AdminDashboardData>> {
    const params: Record<string, string> = {};
    if (filter?.date_from) params.date_from = filter.date_from;
    if (filter?.date_to) params.date_to = filter.date_to;
    if (filter?.department_id) params.department_id = filter.department_id;
    return this.request('/api/reports/dashboard/admin', params);
  }

  async getOfficerDashboard(filter?: DashboardFilter): Promise<ApiResponse<OfficerDashboardData>> {
    const params: Record<string, string> = {};
    if (filter?.date_from) params.date_from = filter.date_from;
    if (filter?.date_to) params.date_to = filter.date_to;
    return this.request('/api/reports/dashboard/officer', params);
  }

  async getSupervisorDashboard(filter?: DashboardFilter): Promise<ApiResponse<SupervisorDashboardData>> {
    const params: Record<string, string> = {};
    if (filter?.date_from) params.date_from = filter.date_from;
    if (filter?.date_to) params.date_to = filter.date_to;
    return this.request('/api/reports/dashboard/supervisor', params);
  }

  async getVendorDashboard(vendorId: string, filter?: DashboardFilter): Promise<ApiResponse<VendorDashboardData>> {
    const params: Record<string, string> = { vendor_id: vendorId };
    if (filter?.date_from) params.date_from = filter.date_from;
    if (filter?.date_to) params.date_to = filter.date_to;
    return this.request('/api/reports/dashboard/vendor', params);
  }

  async getReportTypes(): Promise<ApiResponse<ReportType[]>> {
    return this.request('/api/reports/types');
  }

  async getReportData(reportId: string, filter?: DashboardFilter): Promise<ApiResponse<Record<string, unknown>[]>> {
    const params: Record<string, string> = {};
    if (filter?.date_from) params.date_from = filter.date_from;
    if (filter?.date_to) params.date_to = filter.date_to;
    if (filter?.department_id) params.department_id = filter.department_id;
    if (filter?.status) params.status = filter.status;
    return this.request(`/api/reports/${reportId}`, params);
  }

  async exportToExcel(config: ExportConfig): Promise<ApiResponse<Blob>> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${this.baseUrl}/api/reports/export`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!res.ok) return { success: false, data: null as any, message: 'Export failed' };
    const blob = await res.blob();
    return { success: true, data: blob, message: 'Export generated' };
  }
}
